/* eslint-disable react-hooks/rules-of-hooks */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { employeeService } from "../services/api/employee";
import { EmployeeFormData, FetchEmployeesParams } from "../types/employees";
import toast from "react-hot-toast";

export const useEmployees = (instituteId: number, filters: FetchEmployeesParams = { instituteId, page: 1, limit: 15 }) => {
    const queryClient = useQueryClient();

    const queryKeys = {
        employees: (params: FetchEmployeesParams) => ['employees', params.instituteId, params.page, params.limit, params.search ?? ''],
        employeeById: (instituteId: number, id: number) => ['employee', instituteId, id],
    };

    const employees = useQuery({
        queryKey: queryKeys.employees(filters),
        queryFn: () => employeeService.getAll(filters),
        enabled: !!instituteId,
        staleTime: 30 * 1000,
    });

    const getEmployee = (id: number) => useQuery({
        queryKey: queryKeys.employeeById(instituteId, id),
        queryFn: () => employeeService.getById(id),
        enabled: !!id,
        staleTime: 60 * 1000,
    });
    const addEmployee = useMutation({
        mutationFn: (data: EmployeeFormData) => employeeService.create(data, instituteId),
        onSuccess: () => {
            toast.success("Employee Added Successfully");
            queryClient.invalidateQueries({ queryKey: ['employees', instituteId] });
        },
        onError: (error) =>{
            console.error("Error adding Employee:", error);
        }
    });
    const updateEmployee = useMutation({
        mutationFn: ({ id, data }: { id: number; data: EmployeeFormData }) => employeeService.update(id,data,instituteId),
        onSuccess: (_,variables) => {
            toast.success("Employee updated Successfully");
            queryClient.invalidateQueries({ queryKey: ['employees', instituteId] });
            queryClient.invalidateQueries({ queryKey: queryKeys.employeeById(instituteId, variables.id) });
        },
        onError: (error) =>{
            console.error("Error updating Employee:", error);
        }
    });
    const deleteEmployee = useMutation({
        mutationFn: (id:number)=> employeeService.delete(id),
        onSuccess: () =>{
            toast.success("Employee deleted Successfully");
            queryClient.invalidateQueries({ queryKey: ['employees', instituteId] });
        },
        onError: (error) => {
            console.error("Error deleting Employee:", error);
        }
    });
    const generateAppointmentLetterPreview = useMutation({
        mutationFn: (data: EmployeeFormData) => employeeService.generateAppointmentLetterPreview(instituteId, data),
        onSuccess: (data) => {
            toast.success("Appointment letter generated successfully");
            return data;
        },
        onError: (error) => {
            console.error("Error generating appointment letter preview:", error);
        }
    });
    const getAppointmentLetterUrl = (id: number, options?: { enabled?: boolean }) => useQuery({
        queryKey: ['appointmentLetterUrl', id],
        queryFn: () => employeeService.getAppointmentLetterUrl(id),
        enabled: !!id && (options?.enabled !== false)
    });
    const generateAppointmentLetter = (id: number, options?: { enabled?: boolean }) => useQuery({
        queryKey: ['generateAppointmentLetter', id],
        queryFn: () => employeeService.generateAppointmentLetter(id),
        enabled: !!id && (options?.enabled !== false)
    });

    
    return {
        employees: employees.data?.data ?? [],
        totalCount: employees.data?.totalCount ?? 0,
        isLoading: employees.isLoading,
        error: employees.error,
        getEmployee,
        addEmployee,
        updateEmployee,
        deleteEmployee,
        getAppointmentLetterUrl,
        generateAppointmentLetterPreview,
        generateAppointmentLetter
    };
}